import {
  CommuteAutoCheckoutFlag,
  CommuteDay,
  CommuteUseFlag,
  WorkingTemplateCommuteSettings,
  WorkingTemplateCommuteSettingsApi,
  WorkingTemplateCommuteSettingsInput
} from "../../../api/working-template-commute-settings-api.types";

export type WorkingTemplateCommuteSettingsDraft =
  WorkingTemplateCommuteSettingsInput;
export type CommuteSettingsMode = "create" | "edit";
export type CommuteHydrationStatus = "idle" | "loading" | "ready" | "error";
export type CommuteSaveStatus = "idle" | "saving" | "success" | "error";

export interface CommuteSettingsClientError {
  code: string;
  message: string;
  retryable: boolean;
}

export interface CommuteSettingsState {
  mode: CommuteSettingsMode;
  workingTemplateIdx: number | null;
  draft: WorkingTemplateCommuteSettingsDraft;
  hydrationStatus: CommuteHydrationStatus;
  saveStatus: CommuteSaveStatus;
  dirty: boolean;
  error: CommuteSettingsClientError | null;
  lastPersisted: WorkingTemplateCommuteSettingsDraft | null;
  activeHydrationRequest: number;
  activeSaveRequest: number;
}

export type CommuteSettingsAction =
  | { type: "TARGET_CHANGED"; workingTemplateIdx: number }
  | { type: "HYDRATION_STARTED"; request: number }
  | {
      type: "HYDRATED";
      request: number;
      settings: WorkingTemplateCommuteSettings;
    }
  | {
      type: "HYDRATION_FAILED";
      request: number;
      error: CommuteSettingsClientError;
    }
  | {
      type: "FIELD_CHANGED";
      field: keyof WorkingTemplateCommuteSettingsDraft;
      value: WorkingTemplateCommuteSettingsDraft[keyof WorkingTemplateCommuteSettingsDraft];
    }
  | { type: "SAVE_STARTED"; request: number }
  | {
      type: "SAVED";
      request: number;
      settings: WorkingTemplateCommuteSettings;
    }
  | {
      type: "SAVE_FAILED";
      request: number;
      error: CommuteSettingsClientError;
    };

export type CommuteSettingsValidationErrors = Partial<
  Record<keyof WorkingTemplateCommuteSettingsDraft, string>
>;

export function createCommuteSettingsDraft(): WorkingTemplateCommuteSettingsDraft {
  return {
    pcUse: 0,
    appUse: 0,
    startWorkingMin: 0,
    endWorkingMin: 0,
    approvalDataType: 0,
    dayOfWeek: [],
    unCheckoutAutoCheckoutFlag: 2,
    unCheckoutAutoCheckoutTime: "16:30",
    checkInStartWorkTimeSetFlag: 0,
    checkoutEndWorkTimeSetFlag: 0,
    checkinStartWorkTimeSetFlagAddMin: 540,
    updateEndWorkTimeOnlyFlag: 0,
    checkinAdjustUse: 0,
    checkinAdjustUnitMin: 15,
    checkinAdjustEndTime: "11:00",
    checkinFixedEndUse: 0,
    checkinFixedEndStartTime: "11:00",
    checkinFixedEndTime: "20:00",
    checkinLimitUse: 0,
    checkinLimitTime: "20:00"
  };
}

export function createCommuteSettingsState(
  mode: CommuteSettingsMode,
  workingTemplateIdx?: number
): CommuteSettingsState {
  return {
    mode,
    workingTemplateIdx:
      mode === "edit" && workingTemplateIdx ? workingTemplateIdx : null,
    draft: createCommuteSettingsDraft(),
    hydrationStatus: mode === "edit" ? "idle" : "ready",
    saveStatus: "idle",
    dirty: false,
    error: null,
    lastPersisted: null,
    activeHydrationRequest: 0,
    activeSaveRequest: 0
  };
}

export async function hydrateCommuteSettingsForMode(
  mode: CommuteSettingsMode,
  workingTemplateIdx: number | undefined,
  api: Pick<WorkingTemplateCommuteSettingsApi, "get">,
  signal?: AbortSignal
): Promise<WorkingTemplateCommuteSettings | null> {
  if (mode === "create") return null;
  if (
    !workingTemplateIdx ||
    !Number.isSafeInteger(workingTemplateIdx) ||
    workingTemplateIdx <= 0
  ) {
    throw new RangeError("workingTemplateIdx must be a positive integer");
  }
  const response = await api.get(workingTemplateIdx, signal);
  return response.data;
}

function withoutId(
  settings: WorkingTemplateCommuteSettings
): WorkingTemplateCommuteSettingsDraft {
  const { workingTemplateIdx: _workingTemplateIdx, ...draft } = settings;
  return draft;
}

function sameDraft(
  left: WorkingTemplateCommuteSettingsDraft,
  right: WorkingTemplateCommuteSettingsDraft | null
): boolean {
  if (!right) return false;
  return JSON.stringify(left) === JSON.stringify(right);
}

export function commuteSettingsReducer(
  state: CommuteSettingsState,
  action: CommuteSettingsAction
): CommuteSettingsState {
  switch (action.type) {
    case "TARGET_CHANGED":
      return {
        ...createCommuteSettingsState("edit", action.workingTemplateIdx),
        activeHydrationRequest: state.activeHydrationRequest + 1,
        activeSaveRequest: state.activeSaveRequest + 1
      };
    case "HYDRATION_STARTED":
      return {
        ...state,
        hydrationStatus: "loading",
        error: null,
        activeHydrationRequest: action.request
      };
    case "HYDRATED": {
      if (
        state.activeHydrationRequest !== action.request ||
        state.workingTemplateIdx !== action.settings.workingTemplateIdx
      ) {
        return state;
      }
      const draft = withoutId(action.settings);
      return {
        ...state,
        draft,
        lastPersisted: draft,
        hydrationStatus: "ready",
        saveStatus: "idle",
        dirty: false,
        error: null
      };
    }
    case "HYDRATION_FAILED":
      if (state.activeHydrationRequest !== action.request) return state;
      return { ...state, hydrationStatus: "error", error: action.error };
    case "FIELD_CHANGED": {
      const draft = { ...state.draft, [action.field]: action.value };
      return {
        ...state,
        draft,
        dirty: !sameDraft(draft, state.lastPersisted),
        saveStatus: "idle",
        error: null
      };
    }
    case "SAVE_STARTED":
      return {
        ...state,
        saveStatus: "saving",
        error: null,
        activeSaveRequest: action.request
      };
    case "SAVED": {
      if (
        state.activeSaveRequest !== action.request ||
        (state.workingTemplateIdx !== null &&
          state.workingTemplateIdx !== action.settings.workingTemplateIdx)
      ) {
        return state;
      }
      const draft = withoutId(action.settings);
      return {
        ...state,
        workingTemplateIdx: action.settings.workingTemplateIdx,
        draft,
        lastPersisted: draft,
        hydrationStatus: "ready",
        saveStatus: "success",
        dirty: false,
        error: null
      };
    }
    case "SAVE_FAILED":
      if (state.activeSaveRequest !== action.request) return state;
      return { ...state, saveStatus: "error", error: action.error };
    default:
      return state;
  }
}

function validClock(value: string): boolean {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  return Boolean(
    match && Number(match[1]) <= 23 && Number(match[2]) <= 59
  );
}

export function validateCommuteSettingsDraft(
  draft: WorkingTemplateCommuteSettingsDraft
): CommuteSettingsValidationErrors {
  const errors: CommuteSettingsValidationErrors = {};
  if (
    !Number.isSafeInteger(draft.checkinAdjustUnitMin) ||
    draft.checkinAdjustUnitMin <= 0
  ) {
    errors.checkinAdjustUnitMin = "1 이상의 정수를 입력해 주세요.";
  }
  const timeFields: Array<keyof WorkingTemplateCommuteSettingsDraft> = [
    "unCheckoutAutoCheckoutTime",
    "checkinAdjustEndTime",
    "checkinFixedEndStartTime",
    "checkinFixedEndTime",
    "checkinLimitTime"
  ];
  timeFields.forEach(field => {
    const value = draft[field];
    if (typeof value !== "string" || !validClock(value)) {
      errors[field] = "HH:mm 형식의 시간을 입력해 주세요.";
    }
  });
  return errors;
}

export class CommuteSettingsValidationError extends Error {
  readonly firstField: keyof WorkingTemplateCommuteSettingsDraft | undefined;

  constructor(readonly errors: CommuteSettingsValidationErrors) {
    super("출퇴근 설정 입력값을 확인해 주세요.");
    this.name = "CommuteSettingsValidationError";
    this.firstField = Object.keys(errors)[0] as
      | keyof WorkingTemplateCommuteSettingsDraft
      | undefined;
  }
}

export function serializeCommuteSettingsDraft(
  draft: WorkingTemplateCommuteSettingsDraft
): WorkingTemplateCommuteSettingsInput {
  const errors = validateCommuteSettingsDraft(draft);
  if (Object.keys(errors).length > 0) {
    throw new CommuteSettingsValidationError(errors);
  }
  return {
    ...draft,
    dayOfWeek: [...draft.dayOfWeek]
  };
}

type LegacyUse = "USE" | "UN_USE";
type LegacyAutoCheckout = "UN_USE" | "LEAVING_PLANS_TIME" | "SET_TIME";
type LegacyDay = "SUN" | "MON" | "TUE" | "WED" | "THR" | "FRI" | "SAT";

export interface LegacyCommuteVariables {
  pcUse: LegacyUse;
  appUse: LegacyUse;
  startWorkingMin: number;
  endWorkingMin: number;
  approvalDataType: LegacyUse;
  dayOfWeek: LegacyDay[];
  unCheckoutAutoCheckoutFlag: LegacyAutoCheckout;
  unCheckoutAutoCheckoutTime: string;
  checkInStartWorkTimeSetFlag: LegacyUse;
  checkoutEndWorkTimeSetFlag: LegacyUse;
  checkinStartWorkTimeSetFlagAddMin: number;
  updateEndWorkTimeOnlyFlag: LegacyUse;
}

const DAY_NUMBER: Record<LegacyDay, CommuteDay> = {
  SUN: 1,
  MON: 2,
  TUE: 3,
  WED: 4,
  THR: 5,
  FRI: 6,
  SAT: 7
};

function numericUse(value: LegacyUse): CommuteUseFlag {
  return value === "USE" ? 1 : 0;
}

const AUTO_CHECKOUT_NUMBER: Record<LegacyAutoCheckout, CommuteAutoCheckoutFlag> = {
  UN_USE: 0,
  LEAVING_PLANS_TIME: 1,
  SET_TIME: 2
};

export function legacyCommuteVariablesToDraft(
  legacy: LegacyCommuteVariables,
  recognition: WorkingTemplateCommuteSettingsDraft
): WorkingTemplateCommuteSettingsDraft {
  return {
    ...recognition,
    pcUse: numericUse(legacy.pcUse),
    appUse: numericUse(legacy.appUse),
    startWorkingMin: legacy.startWorkingMin,
    endWorkingMin: legacy.endWorkingMin,
    approvalDataType: numericUse(legacy.approvalDataType),
    dayOfWeek: legacy.dayOfWeek.map(day => DAY_NUMBER[day]),
    unCheckoutAutoCheckoutFlag:
      AUTO_CHECKOUT_NUMBER[legacy.unCheckoutAutoCheckoutFlag],
    unCheckoutAutoCheckoutTime: legacy.unCheckoutAutoCheckoutTime,
    checkInStartWorkTimeSetFlag: numericUse(
      legacy.checkInStartWorkTimeSetFlag
    ),
    checkoutEndWorkTimeSetFlag: numericUse(
      legacy.checkoutEndWorkTimeSetFlag
    ),
    checkinStartWorkTimeSetFlagAddMin:
      legacy.checkinStartWorkTimeSetFlagAddMin,
    updateEndWorkTimeOnlyFlag: numericUse(legacy.updateEndWorkTimeOnlyFlag)
  };
}
